import classNames from 'classnames'
import { Check, ChevronDown } from 'lucide-react'
import { LOCATION_ICONS } from './location-select.domain'
import type { LocationSelectItemProps, LocationSelectProps } from './location-select.interface'
import classes from './location-select.module.scss'
import { useLocationSelect } from './use-location-select'

export function LocationSelect({ value, options, onChange, labelId }: LocationSelectProps) {
  const { rootRef, triggerRef, isOpen, toggle, select, onKeyDown } = useLocationSelect(onChange)
  const selected = options.find((option) => option.value === value) ?? options[0]
  const SelectedIcon = LOCATION_ICONS[selected.value]
  const valueId = `${labelId}-value`

  return (
    <div ref={rootRef} className={classes.root} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        className={classes.trigger}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={`${labelId} ${valueId}`}
        onClick={toggle}
      >
        <span className={classes.triggerValue}>
          <SelectedIcon
            size={20}
            aria-hidden
            className={classNames(classes.triggerIcon, { [classes.triggerIconMuted]: selected.value === 'none' })}
          />
          <span id={valueId}>{selected.label}</span>
        </span>
        <ChevronDown size={22} aria-hidden className={classes.chevron} />
      </button>
      {isOpen && (
        <div role="listbox" aria-labelledby={labelId} className={classes.menu}>
          {options.map((option) => (
            <LocationSelectItem
              key={option.value}
              option={option}
              isSelected={option.value === selected.value}
              onSelect={select}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function LocationSelectItem({ option, isSelected, onSelect }: LocationSelectItemProps) {
  const OptionIcon = LOCATION_ICONS[option.value]

  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      disabled={option.disabled}
      className={classNames(classes.option, {
        [classes.optionSelected]: isSelected,
        [classes.optionDisabled]: option.disabled,
      })}
      onClick={() => onSelect(option)}
    >
      <OptionIcon size={20} aria-hidden className={classes.optionIcon} />
      <span className={classes.optionText}>
        <span className={classes.optionLabel}>{option.label}</span>
        <span className={classes.optionDescription}>{option.description}</span>
      </span>
      {isSelected && <Check size={18} aria-hidden data-testid="location-option-check" className={classes.check} />}
    </button>
  )
}
